import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle, CheckCircle2, ShieldAlert, HeartHandshake, PhoneCall } from "lucide-react";
import Footer from "../components/Footer";

export default function DisclaimerPage() {
  const navigate = useNavigate();

  const restrictions = [
    "Type 1 or Type 2 Diabetes",
    "Chronic Kidney Disease (CKD) or Renal conditions",
    "Cardiovascular diseases or history of stroke",
    "Active pregnancy or lactation",
    "Eating disorders (e.g., anorexia, bulimia)",
    "Severe hypertension or food allergies requiring medical supervision"
  ];

  return (
    <div className="min-h-screen bg-background relative flex flex-col">
      {/* Background pattern */}
      <div className="fixed inset-0 opacity-[0.02] pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIgnTUwMSUiIGhlaWdodD0iMTAwJSIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIwLjA1Ii8+PC9zdmc+')]"></div>

      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <img src="/logo.svg" alt="Nutri Saathi Logo" className="w-6 h-6 object-contain" />
            </div>
            <span className="font-bold text-foreground">Nutri Saathi Trust Center</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-12">
        {/* Page Hero */}
        <div className="text-center max-w-2xl mx-auto mb-12 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 text-accent text-xs font-semibold">
            <AlertTriangle className="w-3.5 h-3.5 animate-pulse" />
            Medical Disclaimer
          </div>
          <h1 className="text-4xl font-extrabold text-foreground font-serif">Health & Wellness Disclaimer</h1>
          <p className="text-muted-foreground">
            Nutri Saathi is committed to providing healthy Indian lifestyle support. Please review our safety guidelines and medical boundaries.
          </p>
          <p className="text-xs text-muted-foreground font-semibold">Please read this document carefully before starting any diet plan.</p>
        </div>

        <div className="space-y-8">
          {/* Main Warning Box */}
          <div className="p-6 md:p-8 rounded-3xl bg-amber-500/10 border-2 border-amber-500/20 shadow-sm space-y-4">
            <div className="flex items-center gap-3">
              <ShieldAlert className="w-8 h-8 text-amber-600 shrink-0" />
              <h2 className="text-xl font-bold text-foreground font-serif">Not Medical Advice</h2>
            </div>
            <p className="text-sm text-foreground/80 leading-relaxed">
              Nutri Saathi is an <strong>AI-powered digital wellness assistant</strong>. All information, including food recommendations, meal schedules, macro counts, calorie calculations, and motivational AI telephone calls, are generated using standard nutritional databases and language models.
            </p>
            <p className="text-sm font-semibold text-foreground/90">
              This application does not provide clinical diagnoses, medical counseling, personalized disease management, or clinical dietetics. The outputs are purely educational and motivational in nature.
            </p>
          </div>

          {/* Conditional Constraints Checklist */}
          <div className="p-6 md:p-8 rounded-3xl bg-card border border-border/50 shadow-sm space-y-6">
            <div className="flex items-center gap-3">
              <HeartHandshake className="w-6 h-6 text-primary shrink-0" />
              <h3 className="text-lg font-bold text-foreground font-serif">Pre-existing Health Conditions</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              If you suffer from or are managing any of the following conditions, you must <strong>not</strong> begin a diet plan suggested by Nutri Saathi without direct approval and supervision from a certified medical physician or registered clinical dietician:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {restrictions.map((cond, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-secondary/15 border border-border/30">
                  <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                  <span className="text-xs font-semibold text-foreground">{cond}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Limits of Generative AI */}
          <div className="p-6 md:p-8 rounded-3xl bg-card border border-border/50 shadow-sm space-y-4">
            <h3 className="text-lg font-bold text-foreground font-serif">Limits of Generative AI Models</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              While we optimize our AI prompts to emphasize standard Indian balanced diet recommendations (such as recommending wholesome grains, dals, and vegetables), generative AI language models are capable of generating errors, hallucinating quantities, or miscalculating caloric breakdowns. 
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Any action you take based on the recommendations of the AI chat, automated calls, or meal suggestions is taken strictly at your own risk. Always use common sense and cross-reference guidelines with verified medical authorities.
            </p>
          </div>

          {/* Contact Details */}
          <div className="p-6 md:p-8 rounded-3xl bg-secondary/20 border border-border/40 text-center space-y-4">
            <h3 className="text-lg font-bold text-foreground font-serif">Questions or Support?</h3>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">
              If you have any doubts regarding the safety of our program, subscription billing, or cancel requests, please email us directly.
            </p>
            <a
              href="mailto:raghav@nutrisaathi.life?subject=NutriSaathi Medical Safety Inquiry"
              className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-all btn-primary"
            >
              <PhoneCall className="w-4 h-4" />
              <span>Contact Support</span>
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
